import React, { ReactNode } from 'react'
import { Typography } from 'antd'
import { styled } from 'styled-components'
import classNames from 'classnames'
import CustomChart from '../chart/CustomChart'
import CustomPieChart from '../chart/CustomPieChart'
import Editor from '../editor/Editor'
import CustomReportSectionActions from './CustomReportSectionActions'
import {
  CustomReportChartSection,
  CustomReportDataSection,
  CustomReportInfoboxSection,
  CustomReportPieSection,
  CustomReportSectionsType,
  CustomReportTableSection
} from '../../../../../redux/context/customReports/typesSection'
import CustomReportAgGrid from '../grid/CustomReportAgGrid'
import CustomReportAgGridTransposed from '../grid/CustomReportAgGridTransposed'
import InfoboxCard from '../infobox/InfoboxCard'
import { getTitleByLanguage } from '../customReportTable/utils'
import { useCalculatedReport } from '../CalculatedReportContext'

interface ReportSectionProps {
  section: CustomReportSectionsType
  editModal: (section: CustomReportSectionsType) => void
}

const ContainerDiv = styled.div`
  width: 100%;
  height: 100%;
  padding: 8px;
`

const HeaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  width: 100%;
`
const ContentDiv = styled.div`
  height: calc(100% - 32px);
  overflow: hidden;

  // Teksti osiolle vain y-scroll
  &.text {
    overflow-y: auto;
  }
`

const InfoboxContainerDiv = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #f0f0f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
  &:focus-visible {
    outline: 2px solid #1890ff;
    outline-offset: -2px;
  }
`
const ReportSection: React.FC<ReportSectionProps> = ({ section, editModal }) => {
  const { data: calculatedReportData } = useCalculatedReport()
  const reportId = calculatedReportData?.id

  if (!section) return null

  const getEvaluatedSection = (s: CustomReportDataSection) => {
    const { categoriesEvaluated, ...rest } = s
    return { ...rest, categories: categoriesEvaluated }
  }

  const renderItem = (sec: CustomReportSectionsType): ReactNode | null => {
    if (!reportId) return null

    switch (sec.type) {
      case 'table': {
        if (sec?.style?.switchRowsAndColumns) {
          return <CustomReportAgGridTransposed section={getEvaluatedSection(sec) as CustomReportTableSection} />
        }
        return <CustomReportAgGrid section={getEvaluatedSection(sec) as CustomReportTableSection} reportId={reportId} />
      }
      case 'graph': {
        return <CustomChart section={getEvaluatedSection(sec) as CustomReportChartSection} />
      }
      case 'pie': {
        return <CustomPieChart section={getEvaluatedSection(sec) as CustomReportPieSection} />
      }
      case 'text': {
        return <Editor editable={false} section={sec} />
      }
      default:
        return null
    }
  }

  if (section.type === 'infobox') {
    return (
      <InfoboxContainerDiv
        role="button"
        tabIndex={0}
        onClick={() => editModal(section)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            editModal(section)
          }
        }}
      >
        <InfoboxCard
          section={getEvaluatedSection(section) as CustomReportInfoboxSection}
          title={getTitleByLanguage(section)}
        />
      </InfoboxContainerDiv>
    )
  }

  return (
    <ContainerDiv>
      <HeaderDiv>
        <Typography.Title style={{ margin: 0 }} level={4}>
          {getTitleByLanguage(section)}
        </Typography.Title>
        <CustomReportSectionActions section={section} editModal={editModal} />
      </HeaderDiv>
      <ContentDiv className={classNames('section-content', `${section.type}`)}>{renderItem(section)}</ContentDiv>
    </ContainerDiv>
  )
}

export default ReportSection
